import { useEffect, useState } from "react";
import {
  JOB_TYPE_LABELS,
  type Job,
} from "@eagledispatch/shared";
import { apiFetch } from "../../lib/api";
import { formatDateTime } from "../../lib/format";
import { parseDecimal, parseDollarsToCents, MAX_TONS } from "../../lib/parse-amount";
import Button from "../../components/Button";
import Card from "../../components/Card";
import StatusTag from "../../components/StatusTag";
import FieldError from "../../components/FieldError";
import LoadPhotosSection from "../../components/office-detail/LoadPhotosSection";
import TicketPhotoCard from "../../components/office-detail/TicketPhotoCard";
import LoadDecisionEditor from "../../components/office-detail/LoadDecisionEditor";
import type { OfficeRoute } from "./OfficeHome";

export default function FinishedJobDetail({
  id,
  onNavigate,
  onReviewed,
}: {
  id: string;
  onNavigate: (route: OfficeRoute) => void;
  onReviewed?: (jobNumber: string) => void;
}) {
  const [job, setJob] = useState<Job | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Review form state
  const [weightTons, setWeightTons] = useState("");
  const [disposalAmount, setDisposalAmount] = useState("");
  const [billableAmount, setBillableAmount] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{
    weightTons?: string;
    disposalAmount?: string;
    billableAmount?: string;
  }>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const back = () => onNavigate({ name: "home" });

  const loadJob = () => {
    setError(null);
    apiFetch<Job>(`/api/v1/jobs/${id}`)
      .then((j) => {
        setJob(j);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  };

  useEffect(() => {
    loadJob();
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!job) return;
    setSubmitError(null);
    setFieldErrors({});

    const isDeliveryOnly = job.jobType === "D_O";

    if (isDeliveryOnly) {
      setSubmitting(true);
      try {
        await apiFetch(`/api/v1/jobs/${id}/review`, {
          method: "POST",
          body: JSON.stringify({}),
        });
        if (onReviewed) onReviewed(job.jobNumber);
        onNavigate({ name: "reviewed-job", id: job.id });
      } catch (err: unknown) {
        setSubmitError(err instanceof Error ? err.message : String(err));
      } finally {
        setSubmitting(false);
      }
      return;
    }

    const errors: Record<string, string> = {};
    const weightNum = parseDecimal(weightTons);
    if (!weightTons.trim() || isNaN(weightNum) || weightNum <= 0) {
      errors.weightTons = "Must be greater than 0";
    } else if (!Number.isFinite(weightNum) || weightNum > MAX_TONS) {
      errors.weightTons = "Amount too large";
    }

    const disposalCents = parseDollarsToCents(disposalAmount);
    if (!disposalAmount.trim() || isNaN(disposalCents) || disposalCents <= 0) {
      errors.disposalAmount = "Must be greater than $0.00";
    } else if (!Number.isFinite(disposalCents) || !Number.isSafeInteger(disposalCents)) {
      errors.disposalAmount = "Amount too large";
    }

    const billableCents = parseDollarsToCents(billableAmount);
    if (!billableAmount.trim() || isNaN(billableCents) || billableCents <= 0) {
      errors.billableAmount = "Must be greater than $0.00";
    } else if (!Number.isFinite(billableCents) || !Number.isSafeInteger(billableCents)) {
      errors.billableAmount = "Amount too large";
    }

    if (!errors.disposalAmount && !errors.billableAmount && billableCents < disposalCents) {
      errors.billableAmount = "Billable amount must be ≥ disposal amount";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setSubmitting(true);
    try {
      await apiFetch(`/api/v1/jobs/${id}/review`, {
        method: "POST",
        body: JSON.stringify({
          weightTons: weightNum,
          disposalAmountCents: disposalCents,
          billableAmountCents: billableCents,
        }),
      });
      if (onReviewed) onReviewed(job.jobNumber);
      onNavigate({ name: "reviewed-job", id: job.id });
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <main className="surface office-surface">
        <header className="surface-header">
          <Button variant="ghost" onClick={back}>
            ← Back to Queue
          </Button>
          <h1>Job Review</h1>
        </header>
        <Card>
          <p className="err">Failed to load job.</p>
          <p className="muted">{error}</p>
        </Card>
      </main>
    );
  }

  if (!job) {
    return (
      <main className="surface office-surface">
        <header className="surface-header">
          <Button variant="ghost" onClick={back}>
            ← Back to Queue
          </Button>
          <h1>Job Review</h1>
        </header>
        <p className="muted">Loading job details…</p>
      </main>
    );
  }

  const snap = (job.customerSnapshot ?? {}) as {
    customer?: Record<string, unknown>;
    property?: Record<string, unknown>;
  };
  const customer = (snap.customer ?? {}) as Record<string, unknown>;
  const property = (snap.property ?? {}) as Record<string, unknown>;
  const address = [property.addressLine1, property.city, property.state, property.zip]
    .filter((x): x is string => typeof x === "string" && x.length > 0)
    .join(", ");

  const isDeliveryOnly = job.jobType === "D_O";

  return (
    <main className="surface office-surface">
      <header className="surface-header">
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Button variant="ghost" onClick={back}>
            ← Back to Queue
          </Button>
          <h1>Job {job.jobNumber}</h1>
          <StatusTag state={job.state} />
        </div>
      </header>

      {/* Warning Banners */}
      {job.photoSource === "GALLERY" && (
        <div className="banner banner-danger" style={{ marginBottom: "16px" }}>
          ⚠️ <strong>Photo from GALLERY</strong> — driver uploaded from camera roll, not live capture.
        </div>
      )}

      {job.queuedOffline && (
        <div className="banner banner-info" style={{ marginBottom: "16px" }}>
          📶 <strong>Queued offline</strong> — driver submitted completion while offline; synced later.
        </div>
      )}

      <div className="office-grid">
        {/* Left Column: Job Info + Photos */}
        <div className="office-col">
          <Card>
            <h2>Customer &amp; Site</h2>
            <dl className="detail-list">
              <div className="detail-row">
                <dt>Company</dt>
                <dd>{String(customer.companyName ?? "—")}</dd>
              </div>
              <div className="detail-row">
                <dt>Site Name</dt>
                <dd>{String(property.siteName ?? "—")}</dd>
              </div>
              <div className="detail-row">
                <dt>Site Address</dt>
                <dd>{address || "—"}</dd>
              </div>
              <div className="detail-row">
                <dt>Contact</dt>
                <dd>
                  {customer.contactName ? String(customer.contactName) : "—"}
                  {customer.contactPhone ? ` · ${customer.contactPhone}` : ""}
                </dd>
              </div>
            </dl>
          </Card>

          <Card>
            <h2>Job Specifications</h2>
            <dl className="detail-list">
              <div className="detail-row">
                <dt>Job Type</dt>
                <dd>{JOB_TYPE_LABELS[job.jobType]}</dd>
              </div>
              <div className="detail-row">
                <dt>Dumpster Size</dt>
                <dd>{job.dumpsterSize ? `${job.dumpsterSize.label} (${job.dumpsterSize.yards} yd)` : "—"}</dd>
              </div>
              <div className="detail-row">
                <dt>Truck</dt>
                <dd>{job.truck ? `Truck #${job.truck.unitNumber}` : "—"}</dd>
              </div>
              <div className="detail-row">
                <dt>Driver</dt>
                <dd>{job.driver?.name ?? "—"}</dd>
              </div>
              <div className="detail-row">
                <dt>Landfill</dt>
                <dd>{job.landfill?.name ?? "—"}</dd>
              </div>
              {job.message && (
                <div className="detail-row">
                  <dt>Message</dt>
                  <dd>{job.message}</dd>
                </div>
              )}
            </dl>
          </Card>

          <TicketPhotoCard jobId={id} ticketPhotoUrl={job.ticketPhotoUrl} />

          {/* Unit #26: LOAD PHOTOS section */}
          <LoadPhotosSection jobId={id} />

          {/* Unit #26: LOAD DECISION panel — SPEC-55: set only after review */}
          <LoadDecisionEditor
            job={job}
            onUpdated={setJob}
            editable={job.state === "REVIEWED"}
          />
        </div>

        {/* Right Column: Review Form */}
        <div className="office-col">
          <Card>
            <h2>Office Review &amp; Money Entry</h2>
            <p className="muted" style={{ fontSize: "13px", marginBottom: "16px" }}>
              Verify weight ticket details and enter billing amounts.
            </p>

            {submitError && (
              <div className="banner banner-danger" style={{ marginBottom: "16px" }}>
                {submitError}
              </div>
            )}

            {isDeliveryOnly ? (
              <div className="do-branch-box">
                <div className="banner banner-info" style={{ marginBottom: "16px" }}>
                  Delivery-only job (D/O) has no disposal scale ticket. Billable amount will be stamped from tenant settings (default $0.00).
                </div>
                <form onSubmit={handleSubmit}>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? "Saving…" : "Save & Mark as Reviewed"}
                  </Button>
                </form>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="form-stack">
                <div className="form-group">
                  <label htmlFor="weightTons">Scale Weight (Tons)*</label>
                  <input
                    id="weightTons"
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder="e.g. 4.50"
                    value={weightTons}
                    onChange={(e) => setWeightTons(e.target.value)}
                    required
                  />
                  <FieldError message={fieldErrors.weightTons} />
                </div>

                <div className="form-group">
                  <label htmlFor="disposalAmount">Disposal Cost ($)*</label>
                  <input
                    id="disposalAmount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    maxLength={12}
                    placeholder="e.g. 350.00"
                    value={disposalAmount}
                    onChange={(e) => setDisposalAmount(e.target.value)}
                    required
                  />
                  <FieldError message={fieldErrors.disposalAmount} />
                </div>

                <div className="form-group">
                  <label htmlFor="billableAmount">Billable Amount to Customer ($)*</label>
                  <input
                    id="billableAmount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    maxLength={12}
                    placeholder="e.g. 850.00"
                    value={billableAmount}
                    onChange={(e) => setBillableAmount(e.target.value)}
                    required
                  />
                  <FieldError message={fieldErrors.billableAmount} />
                  <span className="form-hint">Must be greater than or equal to disposal cost.</span>
                </div>

                <div style={{ marginTop: "20px" }}>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? "Saving…" : "Save & Mark as Reviewed"}
                  </Button>
                </div>
              </form>
            )}
          </Card>

          <Card>
            <h2>Timeline</h2>
            <dl className="detail-list">
              <div className="detail-row">
                <dt>Created</dt>
                <dd>{formatDateTime(job.createdAt)}</dd>
              </div>
              <div className="detail-row">
                <dt>Started</dt>
                <dd>{formatDateTime(job.startAt)}</dd>
              </div>
              <div className="detail-row">
                <dt>Finished</dt>
                <dd>{formatDateTime(job.finishAt)}</dd>
              </div>
              <div className="detail-row">
                <dt>Duration</dt>
                <dd>
                  {job.durationSeconds !== null
                    ? `${Math.round(job.durationSeconds / 60)} min`
                    : "—"}
                </dd>
              </div>
            </dl>
          </Card>
        </div>
      </div>
    </main>
  );
}
